import { sortSection, switchSection, segmentedSection, selectSection } from '@/components/common/filterSections';
import {
  DEFERRAL_FILTER_ALL,
  DEFERRAL_FILTER_ACTIVE,
  DEFERRAL_FILTER_NOT_ACTIVE,
  DEFERRAL_FILTER_PREVIOUS
} from '@/deferralFilters';

/**
 * Builds listing filter sections. Must be called reactively since some
 * sections depend on component state (verifiers, sortOptions, noun).
 *
 * @param {Object} opts
 * @param {Array<{ value: string, text: string }>} [opts.sortOptions]
 * @param {boolean} opts.showVerifierFilter
 * @param {Array<{ id: string, displayName: string }>} [opts.verifiers]
 */
export function buildListingFilterSections({ sortOptions, showVerifierFilter, verifiers }) {
  return [
    sortSection({
      fieldKey: 'sort.field',
      ascendingKey: 'sort.ascending',
      options: [
        { value: 'found', label: 'Date Found' },
        { value: 'deferralEnded', label: 'Deferral End Date' },
        { value: 'title', label: 'Title' },
        { value: 'host', label: 'Host' },
        ...(sortOptions || []).map(o => ({ value: o.value, label: o.text }))
      ],
      directionLabel(field, asc) {
        switch (field) {
          case 'found': return asc ? 'Old → New' : 'New → Old';
          case 'deferralEnded': return asc ? 'Old → New' : 'Recent → Older';
          case 'title':
          case 'host':
            return asc ? 'A → Z' : 'Z → A';
          default: return null;
        }
      }
    }),
    switchSection({ key: 'showUnread', label: `New listings only` }),
    switchSection({ key: 'showVerified', label: 'Include verified listings' }),
    segmentedSection({
      key: 'presence',
      label: 'Extract presence',
      options: [
        { value: 'all', label: 'All' },
        { value: 'missing', label: 'Missing' },
        { value: 'review', label: 'Needs absence review' }
      ]
    }),
    segmentedSection({
      key: 'deferred',
      label: 'Deferral Status',
      options: [
        { value: DEFERRAL_FILTER_ALL, label: 'All' },
        { value: DEFERRAL_FILTER_ACTIVE, label: 'Active' },
        { value: DEFERRAL_FILTER_PREVIOUS, label: 'Ended' },
        { value: DEFERRAL_FILTER_NOT_ACTIVE, label: 'Not active' }
      ]
    }),
    selectSection({
      key: 'deferralEndedWithinDays',
      label: 'Deferral Ended',
      placeholder: 'Any time',
      options: [
        { value: '7', label: 'Last 7 days' },
        { value: '30', label: 'Last 30 days' },
        { value: '90', label: 'Last 90 days' }
      ],
      visible: filters => filters.deferred === DEFERRAL_FILTER_PREVIOUS
    }),
    segmentedSection({
      key: 'source',
      label: 'Listing Source',
      options: [
        { value: 'all', label: 'All' },
        { value: 'airbnb', label: 'Airbnb' },
        { value: 'vrbo', label: 'Vrbo' },
        { value: 'booking', label: 'Booking.com' }
      ]
    }),
    selectSection({
      key: 'verifiedByUserId',
      label: 'Verifier',
      placeholder: 'Anyone',
      options: (verifiers || []).map(u => ({ value: u.id, label: u.displayName })),
      visible: () => showVerifierFilter
    })
  ];
}
